import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Wand2, X, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AIMenuGeneratorModal({ isOpen, onClose, onMenuGenerated }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedMenu, setGeneratedMenu] = useState(null);

  // Fetch templates on mount
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-menu/templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const handleGenerate = async () => {
    const prompt = selectedTemplate ? selectedTemplate.prompt : customPrompt;
    
    if (!prompt.trim()) {
      toast.error('Please select a template or enter a custom prompt');
      return;
    }

    setGenerating(true);

    try {
      const payload = selectedTemplate ? {
        prompt: selectedTemplate.prompt,
        cuisine_type: selectedTemplate.cuisine_type,
        price_range: selectedTemplate.price_range,
        num_categories: selectedTemplate.num_categories,
        num_items_per_category: selectedTemplate.num_items_per_category,
        dietary_options: selectedTemplate.dietary_options || []
      } : {
        prompt: customPrompt,
        num_categories: 5,
        num_items_per_category: 5
      };

      const res = await fetch(`${API_URL}/api/ai-menu/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setGeneratedMenu(data);
        toast.success('AI menu generated successfully! 🎉');
        
        // Pass generated menu to parent
        if (onMenuGenerated) {
          onMenuGenerated(data);
        }
        
        // Close modal after short delay
        setTimeout(() => {
          onClose();
          resetState();
        }, 1500);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate menu');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      toast.error('Failed to generate menu');
    } finally {
      setGenerating(false);
    }
  };

  const resetState = () => {
    setSelectedTemplate(null);
    setCustomPrompt('');
    setGeneratedMenu(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="w-6 h-6 text-purple-600" />
            AI Menu Generator
          </DialogTitle>
        </DialogHeader>

        {generatedMenu ? (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-700">
              <strong>Success!</strong> Generated {generatedMenu.stats?.categories} categories with {generatedMenu.stats?.items} items. 
              Redirecting to review page...
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-6">
            {/* Templates Section */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Quick Start Templates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedTemplate?.id === template.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold">{template.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {template.prompt}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {template.cuisine_type}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.price_range}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.num_categories} categories
                          </Badge>
                        </div>
                      </div>
                      {selectedTemplate?.id === template.id && (
                        <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300" />
              <span className="text-sm text-muted-foreground font-medium">OR</span>
              <div className="flex-1 border-t border-gray-300" />
            </div>

            {/* Custom Prompt */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Custom Prompt</h3>
              <Label>Describe your ideal menu</Label>
              <Textarea
                value={customPrompt}
                onChange={(e) => {
                  setCustomPrompt(e.target.value);
                  if (e.target.value) setSelectedTemplate(null);
                }}
                placeholder="Example: Create a modern fusion menu combining Japanese sushi with Mexican flavors. Include 4 categories with 6 items each. Price range $15-35. Add vegetarian and gluten-free options."
                rows={5}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-2">
                <strong>Tips:</strong> Be specific about cuisine, categories, price range, dietary options, and style
              </p>
            </div>

            {/* Generate Button */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose} disabled={generating}>
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || (!selectedTemplate && !customPrompt.trim())}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Menu with AI
                  </>
                )}
              </Button>
            </div>

            {/* Info Alert */}
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription className="text-xs">
                AI will generate a complete menu with categories, items, descriptions, and pricing. 
                You can review and edit before applying to your restaurant.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
